import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  CheckCircledIcon,
  Cross2Icon,
  ExclamationTriangleIcon,
  InfoCircledIcon,
} from '@radix-ui/react-icons'
import { cn } from '@/lib/utils'

const ToastContext = createContext(null)

const TOAST_LIMIT = 3
const DEFAULT_DURATION_MS = 4500

const toastStyles = {
  success: {
    icon: CheckCircledIcon,
    iconClass: 'text-emerald-600 dark:text-emerald-400',
    borderClass: 'border-emerald-200 dark:border-emerald-900/70',
  },
  error: {
    icon: ExclamationTriangleIcon,
    iconClass: 'text-destructive',
    borderClass: 'border-destructive/40',
  },
  info: {
    icon: InfoCircledIcon,
    iconClass: 'text-primary',
    borderClass: 'border-border',
  },
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const show = useCallback((toast) => {
    const id = toast.id ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
    const duration = toast.duration ?? DEFAULT_DURATION_MS

    setToasts((current) => [{ ...toast, id, duration }, ...current].slice(0, TOAST_LIMIT))
    return id
  }, [])

  const api = useMemo(
    () => ({
      show,
      success: (title, options = {}) => show({ ...options, title, variant: 'success' }),
      error: (title, options = {}) => show({ ...options, title, variant: 'error' }),
      info: (title, options = {}) => show({ ...options, title, variant: 'info' }),
      dismiss,
    }),
    [dismiss, show],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

function ToastViewport({ toasts, onDismiss }) {
  return (
    <div
      className="fixed right-4 top-4 z-50 flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
      aria-live="polite"
      aria-relevant="additions text"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

function ToastItem({ toast, onDismiss }) {
  const variant = toast.variant ?? 'info'
  const styles = toastStyles[variant] ?? toastStyles.info
  const Icon = styles.icon

  useEffect(() => {
    if (toast.duration === Infinity) return undefined
    const timer = window.setTimeout(() => onDismiss(toast.id), toast.duration)
    return () => window.clearTimeout(timer)
  }, [onDismiss, toast.duration, toast.id])

  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      className={cn(
        'pointer-events-auto flex items-start gap-3 rounded-lg border bg-background p-3 text-sm text-foreground shadow-lg',
        styles.borderClass,
      )}
    >
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', styles.iconClass)} />
      <div className="min-w-0 flex-1">
        <p className="font-medium leading-5">{toast.title}</p>
        {toast.description && (
          <p className="mt-0.5 text-xs leading-5 text-muted-foreground">{toast.description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-label="Dismiss notification"
      >
        <Cross2Icon className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return context
}
